
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Profile, EstablishmentSettings } from "@/types/profile.types";

export const useProfile = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const handleError = (error: any, context: string) => {
    console.error(`${context}:`, error);
    
    let errorMessage = "Une erreur est survenue lors du chargement du profil.";
    
    if (error.message?.includes("infinite recursion")) {
      errorMessage = "Erreur d'accès aux données. Veuillez vous reconnecter.";
    } else if (error.message?.includes("Invalid Refresh Token")) {
      errorMessage = "Votre session a expiré. Veuillez vous reconnecter.";
    }

    toast({
      variant: "destructive",
      title: "Erreur",
      description: errorMessage,
    });

    if (error.message?.includes("JWT expired")) {
      supabase.auth.signOut().then(() => {
        navigate("/auth/signin");
      });
    }

    throw error;
  };

  const { data: profile, error, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) throw sessionError;
      if (!session) {
        navigate("/auth/signin");
        return null;
      }

      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .maybeSingle();

        if (profileError) throw profileError;

        if (!profile) {
          const { data: newProfile, error: insertError } = await supabase
            .from('profiles')
            .upsert({
              id: session.user.id,
              email: session.user.email,
              type: 'patient',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .select()
            .single();

          if (insertError) throw insertError;
          return newProfile;
        }

        return profile;
      } catch (error) {
        handleError(error, "Profile loading error");
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // Keep fresh for 5 minutes
    gcTime: 1000 * 60 * 30, // Cache for 30 minutes
    retry: 1,
  });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {
      if (event === 'SIGNED_OUT') {
        navigate("/auth/signin");
        queryClient.clear(); // Clear the cache when user signs out
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, queryClient]);

  const updateProfile = async (updates: Partial<Omit<Profile, 'id' | 'created_at'>>) => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user logged in");

      // Ensure type is preserved during update
      const currentType = profile?.type || 'patient';
      
      const { error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          type: currentType, // Always include the type
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;

      // Invalidate and refetch profile data
      queryClient.invalidateQueries({ queryKey: ['profile'] });

      toast({
        title: "Succès",
        description: "Vos informations ont été enregistrées avec succès.",
      });
    } catch (error: any) {
      handleError(error, "Profile update error");
    } finally {
      setSaving(false);
    }
  };

  return {
    loading: isLoading,
    saving,
    error,
    fullName: profile?.full_name || "",
    email: profile?.email || "",
    phone: profile?.phone || profile?.phone_number || "",
    address: profile?.address || profile?.practice_address || "",
    website: profile?.website || "",
    bio: profile?.about_me || "",
    type: profile?.type || "patient",
    profilePhoto: profile?.profile_photo || "",
    setFullName: (name: string) => updateProfile({ full_name: name }),
    setPhone: (phone: string) => updateProfile({ phone, phone_number: phone }),
    setAddress: (address: string) => updateProfile({ address }),
    setWebsite: (website: string) => updateProfile({ website }),
    setBio: (bio: string) => updateProfile({ about_me: bio }),
    updateProfile: (updates: Partial<Profile>) => updateProfile(updates),
  };
};
